import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, RotateCw, Loader2, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { gamesApi } from '../lib/api';
import { toast } from 'sonner';

const PASS_MARK = 3; // out of 5 correct to count as a "win"

export default function GameTrivia() {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [idx, setIdx] = useState(0);
    const [selected, setSelected] = useState(null);
    const [reveal, setReveal] = useState(false);
    const [correct, setCorrect] = useState(0);
    const [done, setDone] = useState(false);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await gamesApi.getTrivia();
            setQuestions(res.data.questions || []);
        } catch (err) {
            toast.error('Could not load trivia');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const q = questions[idx];

    const pick = (opt) => {
        if (reveal) return;
        setSelected(opt);
        setReveal(true);
        if (opt === q.answer) setCorrect(c => c + 1);
    };

    const next = async () => {
        if (idx + 1 >= questions.length) {
            setDone(true);
            const finalCorrect = correct; // already includes current if right
            if (finalCorrect >= PASS_MARK) {
                setSaving(true);
                try {
                    await gamesApi.recordWin('trivia', null, finalCorrect);
                    toast.success(`🎉 You passed! +1 win recorded.`);
                } catch {
                    toast.error('Could not record win');
                } finally {
                    setSaving(false);
                }
            }
        } else {
            setIdx(i => i + 1);
            setSelected(null);
            setReveal(false);
        }
    };

    const restart = async () => {
        setIdx(0);
        setSelected(null);
        setReveal(false);
        setCorrect(0);
        setDone(false);
        await load();
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center text-white pt-20">
                <Loader2 className="w-6 h-6 animate-spin" />
            </div>
        );
    }

    if (done) {
        const passed = correct >= PASS_MARK;
        return (
            <div className="min-h-screen bg-gradient-to-b from-emerald-950 via-teal-950 to-slate-950 text-white pb-20" data-testid="trivia-done">
                <div className="max-w-2xl mx-auto px-4 pt-24">
                    <div className={`rounded-3xl p-8 text-center border ${passed ? 'bg-emerald-500/20 border-emerald-400/40' : 'bg-slate-800/40 border-white/10'}`}>
                        <div className="text-6xl mb-2">{passed ? '🎉' : '💫'}</div>
                        <div className="text-3xl font-bold" data-testid="trivia-final-score">
                            {correct} / {questions.length}
                        </div>
                        <div className="text-sm text-slate-300 mt-2">
                            {passed
                                ? saving ? 'Recording win…' : `Nice! You needed ${PASS_MARK} to pass — win added to your rewards.`
                                : `You need ${PASS_MARK}+ correct to score a win. Try again!`}
                        </div>
                        <div className="flex gap-2 justify-center mt-6">
                            <Button onClick={restart} data-testid="trivia-play-again">
                                <RotateCw className="w-4 h-4 mr-2" /> Play again
                            </Button>
                            <Link to="/games">
                                <Button variant="outline">Back to Games</Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-emerald-950 via-teal-950 to-slate-950 text-white pb-20" data-testid="trivia-game">
            <div className="sticky top-0 z-10 bg-slate-950/80 backdrop-blur border-b border-white/10">
                <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Link to="/games" className="flex items-center gap-1 text-sm text-slate-300 hover:text-white" data-testid="trivia-back">
                        <ArrowLeft className="w-4 h-4" /> Games
                    </Link>
                    <div className="text-sm text-slate-400" data-testid="trivia-progress">
                        {idx + 1} / {questions.length} · Score {correct}
                    </div>
                </div>
            </div>

            <div className="max-w-2xl mx-auto px-4 pt-6">
                <div className="w-full h-1 bg-white/10 rounded-full mb-6 overflow-hidden">
                    <div
                        className="h-full bg-gradient-to-r from-emerald-400 to-teal-400 transition-all"
                        style={{ width: `${((idx + 1) / questions.length) * 100}%` }}
                    />
                </div>

                <div className="rounded-3xl bg-white/5 border border-white/10 p-6" data-testid="trivia-question-card">
                    <div className="text-xs uppercase tracking-widest text-emerald-300 mb-2">Question {idx + 1}</div>
                    <h2 className="text-xl font-bold mb-6">{q.prompt}</h2>
                    <div className="space-y-2">
                        {q.options.map((opt, i) => {
                            const isSel = selected === opt;
                            const isCorrect = reveal && opt === q.answer;
                            const isWrongSel = reveal && isSel && opt !== q.answer;
                            return (
                                <button
                                    key={opt}
                                    onClick={() => pick(opt)}
                                    disabled={reveal}
                                    data-testid={`trivia-option-${i}`}
                                    className={`w-full text-left rounded-xl px-4 py-3 border transition-all flex items-center justify-between ${
                                        isCorrect
                                            ? 'bg-emerald-500/30 border-emerald-400'
                                            : isWrongSel
                                            ? 'bg-rose-500/30 border-rose-400'
                                            : isSel
                                            ? 'bg-white/20 border-white/30'
                                            : 'bg-white/5 border-white/10 hover:bg-white/10'
                                    } disabled:cursor-default`}
                                >
                                    <span>{opt}</span>
                                    {isCorrect && <Check className="w-4 h-4 text-emerald-300" />}
                                    {isWrongSel && <X className="w-4 h-4 text-rose-300" />}
                                </button>
                            );
                        })}
                    </div>

                    {reveal && (
                        <Button onClick={next} className="w-full mt-6 bg-gradient-to-r from-emerald-500 to-teal-500" data-testid="trivia-next-btn">
                            {idx + 1 >= questions.length ? 'See results' : 'Next question'}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
